import { useState, useEffect } from 'react';
import { Pane, SelectMenu, Button, toaster, AddToFolderIcon } from 'evergreen-ui';
import sectionServices from '../services/sectionServices';
import subsectionServices from '../services/subsectionServices';
import FileUploaderSingleUploadDemo from '../commons/FileUploaderSingleUploadDemo';
import attachmentServices from '../services/attachmentServices';
import '../styles/responsive.css';

const AddAttachmentForm = ({ newAttachment, setNewAttachment, setIsAdding, setAttachments }) => {
    const [sections, setSections] = useState([]);
    const [subsections, setSubsections] = useState([]);
    const [isUploading, setIsUploading] = useState(false);

    useEffect(() => {
        const fetchSections = async () => {
            try {
                const sectionsData = await sectionServices.fetchSections();
                setSections(sectionsData);
            } catch (error) {
                console.error('Errore nel recupero delle sezioni:', error);
                toaster.danger('Errore nel recupero delle sezioni.');
            }
        };

        fetchSections();
    }, []);

    useEffect(() => {
        setNewAttachment((prev) => ({ ...prev, section: null }));
    }, [setNewAttachment]);

    const handleFileChange = (file) => {
        setNewAttachment((prev) => ({ ...prev, file }));
    };

    const handleSectionSelect = (sectionId) => {
        setNewAttachment((prev) => ({ ...prev, section: sectionId }));
        (async () => {
            try {
                const subs = await subsectionServices.fetchSubsections(sectionId);
                setSubsections(subs || []);
                setNewAttachment((prev) => ({ ...prev, subsection: null }));
            } catch (e) {
                console.error('Errore nel recupero delle sottocartelle', e);
            }
        })();
    };

    const handleSubmit = async () => {
        if (isUploading) {
            return;
        }

        if (!newAttachment.file) {
            toaster.warning('Nessun file selezionato. Per favore, carica un file.');
            return;
        }

        if (!newAttachment.section) {
            toaster.warning('Seleziona un file e una sezione.');
            return;
        }

        setIsUploading(true);
        try {
            const uploadedAttachment = await attachmentServices.uploadAttachment(newAttachment.file, newAttachment.section, newAttachment.subsection);
            // Il backend risponde {success:false, message} per errori "gestiti" (es. tipo file
            // non consentito) senza lanciare un errore HTTP: un controllo di sola verità qui
            // tratterebbe anche questo caso come successo, aggiungendo un allegato fantasma
            // (icona senza nome, perché mancano id/name/url) e chiudendo comunque il form.
            if (uploadedAttachment?.success && uploadedAttachment?.id) {
                setAttachments(uploadedAttachment);
                toaster.success('Allegato aggiunto con successo.');
                setIsAdding(false);
            } else {
                toaster.danger(uploadedAttachment?.message || 'Errore: l\'allegato caricato non è valido.');
            }
        } catch (error) {
            console.error('Errore durante il caricamento dell\'allegato:', error);
            toaster.danger('Errore durante il caricamento dell\'allegato.');
            setNewAttachment((prev) => ({ ...prev, file: null }));
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <Pane>
            <FileUploaderSingleUploadDemo 
                onFileChange={handleFileChange} 
                marginBottom={16}
            />
            <Pane display="flex" alignItems="center" flexWrap="wrap" gap={16} className="guest-select-row">
                <SelectMenu
                    title="Seleziona una cartella"
                    options={sections.map((section) => ({ label: section.name, value: section.id }))}
                    selected={newAttachment.section}
                    onSelect={(item) => handleSectionSelect(item.value)}
                >
                    <Button className="guest-select-btn">
                        {
                            sections.find((section) => section.id === newAttachment.section)?.name || 'Seleziona una cartella'
                        }
                    </Button>
                </SelectMenu>

                <SelectMenu
                    title="Seleziona una sottocartella (opzionale)"
                    options={subsections.map((s) => ({ label: s.name, value: s.id }))}
                    selected={newAttachment.subsection}
                    onSelect={(item) => setNewAttachment((prev) => ({ ...prev, subsection: item.value }))}
                >
                    <Button className="guest-select-btn">
                        {subsections.find((s) => s.id === newAttachment.subsection)?.name || 'Seleziona una sottocartella (opzionale)'}
                    </Button>
                </SelectMenu>
                <Button appearance="primary" intent="success" onClick={handleSubmit} isLoading={isUploading}>
                    Aggiungi documento <AddToFolderIcon marginLeft={8} />
                </Button>
            </Pane>
        </Pane>
    );
};

export default AddAttachmentForm;